import React, { useCallback, useEffect, useState } from 'react';
import {
  ArchiveBoxIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal';
import AnlagenAssistant from '../features/anlagen/AnlagenAssistant';
import AnlagenInventory from '../features/anlagen/AnlagenInventory';
import AnlagenKnowledge from '../features/anlagen/AnlagenKnowledge';
import BuchungsModal from '../features/anlagen/BuchungsModal';
import {
  Anlage,
  AnlagenForm,
  AnlagenTab,
  Einordnung,
  KatalogEintrag,
  Kandidat,
  Status
} from '../features/anlagen/types';
import { saubereBezeichnung } from '../features/anlagen/utils';
import apiClient from '../utils/apiClient';
import useFinanceDesignContext from '../hooks/useFinanceDesignContext';

const startForm: AnlagenForm = {
  bezeichnung: '',
  kategorie: 'sonstige',
  sphaere: 'ideell',
  menge: 1,
  selbststaendig_nutzbar: true,
  ust_satz: 19,
  nutzungsdauer: 10,
  inbetriebnahme: '',
  standort: '',
  notiz: ''
};

const AdminAnlagen: React.FC = () => {
  useFinanceDesignContext();
  const [tab, setTab] = useState<AnlagenTab>('pruefen');
  const [status, setStatus] = useState<Status | null>(null);
  const [kandidaten, setKandidaten] = useState<Kandidat[]>([]);
  const [katalog, setKatalog] = useState<KatalogEintrag[]>([]);
  const [anlagen, setAnlagen] = useState<Anlage[]>([]);
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [form, setForm] = useState<AnlagenForm>(startForm);
  const [vorschau, setVorschau] = useState<Einordnung | null>(null);
  const [modalBuchung, setModalBuchung] = useState<number | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState<{ visible: boolean; type: 'info' | 'error' | 'warning'; title: string; message: string }>({
    visible: false,
    type: 'info',
    title: '',
    message: ''
  });

  const aktuell = kandidaten[0];

  const zeigeMeldung = useCallback((type: 'info' | 'error' | 'warning', title: string, message: string) => {
    setMeldung({ visible: true, type, title, message });
  }, []);

  const ladeAlles = useCallback(async () => {
    setLaedt(true);
    try {
      const [statusAntwort, kandidatenAntwort, katalogAntwort, anlagenAntwort] = await Promise.all([
        apiClient.get('/api/anlagen/assistent/status'),
        apiClient.get('/api/anlagen/assistent/kandidaten'),
        apiClient.get('/api/anlagen/katalog'),
        apiClient.get('/api/anlagen')
      ]);
      setStatus(statusAntwort.data.data);
      setKandidaten(kandidatenAntwort.data.data || []);
      setKatalog(katalogAntwort.data.data || []);
      setAnlagen(anlagenAntwort.data.data || []);
      setJahr(Number(anlagenAntwort.data.jahr) || new Date().getFullYear());
    } catch (error: any) {
      zeigeMeldung('error', 'Anlagenverwaltung nicht geladen', error.response?.data?.message || 'Bitte prüfe Backend und Datenbankmigration.');
    } finally {
      setLaedt(false);
    }
  }, [zeigeMeldung]);

  useEffect(() => { ladeAlles(); }, [ladeAlles]);

  useEffect(() => {
    if (!aktuell) {
      setForm(startForm);
      setVorschau(null);
      return;
    }
    setForm({
      ...startForm,
      bezeichnung: saubereBezeichnung(aktuell.verwendungszweck, aktuell.name_zahlungsbeteiligter),
      ust_satz: Number((status as any)?.config?.standard_ust || 19),
      inbetriebnahme: aktuell.buchungstag?.slice(0, 10) || ''
    });
    setVorschau(aktuell.einordnung);
  }, [aktuell, status]);

  useEffect(() => {
    if (!aktuell) return;
    let aktiv = true;
    apiClient.post('/api/anlagen/einordnung', {
      brutto: aktuell.brutto,
      ustSatz: form.ust_satz,
      vorsteuerabzug: false,
      menge: form.menge,
      selbststaendigNutzbar: form.selbststaendig_nutzbar
    }).then((response) => aktiv && setVorschau(response.data.data)).catch(() => undefined);
    return () => { aktiv = false; };
  }, [aktuell, form.menge, form.selbststaendig_nutzbar, form.ust_satz]);

  const entscheiden = async (
    entscheidung: 'anlage' | 'kein_anlagegut' | 'umbuchung' | 'zurueckgestellt',
    notiz?: string
  ) => {
    if (!aktuell) return;
    if (entscheidung === 'anlage' && !form.bezeichnung.trim()) {
      zeigeMeldung('warning', 'Bezeichnung fehlt', 'Gib dem Gegenstand einen kurzen, verständlichen Namen.');
      return;
    }
    setSpeichert(true);
    try {
      const body: any = { buchung_id: aktuell.id, entscheidung, notiz };
      if (entscheidung === 'anlage') {
        body.anlage = {
          ...form,
          brutto: aktuell.brutto,
          anschaffungsdatum: aktuell.buchungstag?.slice(0, 10),
          art: vorschau?.art,
          nutzungsdauer_quelle: katalog.find((item) => item.key === form.kategorie)?.quelle
        };
      }
      await apiClient.post('/api/anlagen/assistent/entscheidung', body);
      setKandidaten((alt) => alt.filter((item) => item.id !== aktuell.id));
      const statusAntwort = await apiClient.get('/api/anlagen/assistent/status');
      setStatus(statusAntwort.data.data);
      if (entscheidung === 'anlage') {
        const anlagenAntwort = await apiClient.get('/api/anlagen');
        setAnlagen(anlagenAntwort.data.data || []);
      }
    } catch (error: any) {
      zeigeMeldung('error', 'Entscheidung nicht gespeichert', error.response?.data?.message || 'Unbekannter Fehler.');
    } finally {
      setSpeichert(false);
    }
  };

  const tabs: { key: AnlagenTab; label: string; kurz: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }[] = [
    { key: 'pruefen', label: 'Buchungen prüfen', kurz: `${status?.offen || 0} offen`, icon: ClipboardDocumentCheckIcon },
    { key: 'bestand', label: 'Anlagenverzeichnis', kurz: `${status?.anlagen_im_bestand || anlagen.length} Einträge`, icon: TableCellsIcon },
    { key: 'wissen', label: 'Grundlagen & Einstellungen', kurz: 'Hilfe', icon: BookOpenIcon }
  ];

  return (
    <div className="finance-page mx-auto max-w-7xl pb-10">
      <header className="app-page-header finance-page-header mb-5">
        <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[1fr_360px] lg:items-center">
          <div>
            <div className="flex items-center gap-3">
              <span className="finance-header-icon !h-12 !w-12 !basis-12"><ArchiveBoxIcon /></span>
              <div>
                <p className="finance-kicker">Finanzen · Vereinsvermögen</p>
                <h1 className="finance-title">Anlagenverwaltung</h1>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Der Assistent führt dich Buchung für Buchung durch die Entscheidung. Du brauchst keine Buchhaltungskenntnisse: Jede Frage erklärt, warum sie wichtig ist und was anschließend passiert.
            </p>
          </div>
          <div className="rounded-2xl border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
            <div className="flex gap-3">
              <CheckBadgeIcon className="h-6 w-6 shrink-0 text-primary-700 dark:text-primary-300" />
              <div><p className="text-sm font-bold text-gray-900 dark:text-white">Du behältst die Kontrolle</p><p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">Regeln und KI geben Hinweise. Gespeichert wird erst, wenn du eine Entscheidung bestätigst.</p></div>
            </div>
          </div>
        </div>
      </header>

      <nav className="mb-5 grid gap-2 sm:grid-cols-3" role="tablist" aria-label="Bereiche der Anlagenverwaltung">
        {tabs.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={tab === item.key}
            aria-controls={`anlagen-panel-${item.key}`}
            onClick={() => setTab(item.key)}
            className={`flex min-h-[58px] items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${tab === item.key ? 'border-primary-300 bg-primary-50 text-primary-800 shadow-sm dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-200' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            <span className="min-w-0"><span className="block truncate text-sm font-bold">{item.label}</span><span className="block text-xs opacity-70">{item.kurz}</span></span>
          </button>
        ))}
      </nav>

      <main id={`anlagen-panel-${tab}`} role="tabpanel">
        {tab === 'pruefen' && (
          <AnlagenAssistant
            status={status}
            kandidat={aktuell}
            form={form}
            setForm={setForm}
            vorschau={vorschau}
            katalog={katalog}
            laedt={laedt}
            speichert={speichert}
            onDetails={() => aktuell && setModalBuchung(aktuell.id)}
            onEntscheiden={entscheiden}
            onZumBestand={() => setTab('bestand')}
          />
        )}
        {tab === 'bestand' && (
          <AnlagenInventory
            anlagen={anlagen}
            katalog={katalog}
            jahr={jahr}
            onBuchung={setModalBuchung}
            onReload={ladeAlles}
            onMessage={zeigeMeldung}
          />
        )}
        {tab === 'wissen' && <AnlagenKnowledge status={status} onSaved={ladeAlles} onMessage={zeigeMeldung} />}
      </main>

      <BuchungsModal
        buchungId={modalBuchung}
        onClose={() => setModalBuchung(null)}
        onEntfernt={ladeAlles}
        onMessage={zeigeMeldung}
      />

      <ConfirmationModal
        visible={meldung.visible}
        type={meldung.type}
        title={meldung.title}
        message={meldung.message}
        variant="single"
        button={{ text: 'OK', onPress: () => setMeldung((alt) => ({ ...alt, visible: false })) }}
      />
    </div>
  );
};

export default AdminAnlagen;
